import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Input,
    LinearProgress,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import React from "react";
import {useSnackbar} from "notistack";
import {makeRequest} from "../../services/requestService";
import {API_URL} from "../../constants";
import SearchIcon from "@mui/icons-material/Search";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {makeStyles} from "@mui/styles";
import PropTypes from "prop-types";
import NoResult from "./NoResult";
import InfiniteScroll from "react-infinite-scroll-component";
import {NODE_TYPE} from "../TreeDataViewer/useTree";


export function SearchInput(props) {
    const {onSubmit, ...other} = props;
    const {t} = useTranslation("common");
    const [search, setSearch] = React.useState("");
    const [elevation, setElevation] = React.useState(0);

    const handleKeyDown = (event) => {
        if (event.which === 13) {
            event.preventDefault();
            onSubmit(search);
        }
    }

    return (
        <Paper
            sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: "100%",
                minWidth: "100px",
                maxWidth: "400px",
                height: 44,
                backgroundColor: props.bgcolor ? props.bgcolor : theme => theme.palette.background.input
            }}
            elevation={elevation}
        >
            <Input
                {...other}
                sx={{ml: 1, flex: 1}}
                placeholder={t("main.components.search.placeholder")}
                inputProps={{'aria-label': 'search'}}
                disableUnderline={true}
                onKeyDown={handleKeyDown}
                onChange={(event) => setSearch(event.target.value)}
                value={search}
                onFocus={() => setElevation(3)}
                onBlur={() => setElevation(0)}
            />
            <IconButton onClick={() => onSubmit(search)} sx={{p: '10px'}} size="small" aria-label="search">
                <SearchIcon fontSize="small"/>
            </IconButton>
        </Paper>
    );
}

SearchInput.propTypes = {
    onSubmit: PropTypes.func
}

SearchInput.defaultProps = {
    onSubmit: () => {
    }
}

const useStyles = makeStyles((theme) => ({
    link: {
        textDecoration: "none",
        color: theme.palette.text.primary,
        "&:hover": {
            color: theme.palette.primary.main,
        }
    },
}));

export function TextResultCard(props) {
    const classes = useStyles();
    const {t} = useTranslation("common");
    const {text, onRedirect, ...other} = props;
    if (!text) return null;
    return (
        <Card {...other} variant="outlined"
              sx={{
                  minWidth: "fit-content",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  ":hover": {
                      boxShadow: theme => theme.palette.mode === "light" ? "0 0 15px rgba(33,33,33,.2)" : "0 0 15px rgba(250,250,250,.2)"
                  }
              }}>
            <CardContent>
                <Typography>Page:
                    <Link
                        className={classes.link}
                        onClick={() => onRedirect()}
                        to={`/page/${text.region.page}`}>
                        {" " + text.region.page}
                    </Link>
                </Typography>
                <Typography>{t("main.components.search.paragraph")}:
                    <Link
                        className={classes.link}
                        onClick={() => onRedirect()}
                        to={`/page/${text.region.page}?focused=${NODE_TYPE.TEXT}-${text.id}`}>
                        {" " + text.id}
                    </Link>
                </Typography>
                <Typography>
                    {text.cachedContent.substr(0, 128)}
                    <Tooltip title={text.cachedContent.substr(128)}>
                        <span>{text.cachedContent.length > 128 ? "..." : ""}</span>
                    </Tooltip>
                </Typography>
            </CardContent>
        </Card>
    );
}

TextResultCard.defaultProps = {
    onRedirect: () => null
}


export default function SearchDialog(props) {
    const {open, onClose, value} = props;
    const {t} = useTranslation("common");
    const [inputValue, setInputValue] = React.useState(value);
    const [searchValue, setSearchValue] = React.useState(value);
    const [submit, setSubmit] = React.useState(false);
    const [resultData, setResultData] = React.useState([]);
    const SIZE = 42;
    const [currentPage, setCurrentPage] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(2)
    const [maxResult, setMaxResult] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const {enqueueSnackbar} = useSnackbar();

    React.useEffect(() => {
        if (open) {
            setInputValue(value);
            setSearchValue(value);
            setResultData([]);
            setCurrentPage(0)
            setTotalPages(2)
            setMaxResult(0);
            setSubmit(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    React.useEffect(() => {
        if (submit)
            fetchSearch(0);
        setSubmit(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, submit]);

    const fetchSearch = (page = 0) => {
        if (page === 0)
            setLoading(true);
        makeRequest(`${API_URL}/text/search/?query=${searchValue}&size=${SIZE}&page=${page}`).then(response => {
            if (response.status === 200) {
                if (Array.isArray(response.data.data)) {
                    if (page === 0)
                        setResultData(response.data.data);
                    else
                        setResultData(prevState => [...prevState, ...response.data.data]);
                }
                setCurrentPage(response.data.currentPage);
                setTotalPages(response.data.totalPages);
                setMaxResult(response.data.totalItems);
                setLoading(false);
            } else {
                enqueueSnackbar(t("main.components.search.error"), {variant: "error"});
            }
        });
    }

    const handleChangeSearchValue = (event) => {
        setInputValue(event.target.value);
    }

    const handleSubmit = (event) => {
        setSearchValue(event.target.value);
        setSubmit(true);
    }

    const handleClose = () => {
        onClose();
    }


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            scroll="paper"
            maxWidth="lg"
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
        >
            <DialogTitle id="scroll-dialog-title" sx={{
                bgcolor: theme => theme.palette.appBar.light
            }}>
                <Grid item xs={12} sm={12} md={12} display="flex" justifyContent="center" alignItems="center">
                    <SearchInput value={inputValue} onChange={handleChangeSearchValue} onSubmit={handleSubmit}/>
                </Grid>
            </DialogTitle>
            <DialogContent id="searchContent" dividers>
                <InfiniteScroll
                    next={() => fetchSearch(currentPage + 1)}
                    hasMore={currentPage + 1 < totalPages}
                    dataLength={resultData.length}
                    scrollableTarget="searchContent"
                    style={{overflow: "hidden"}}
                    loader={!loading && searchValue && <LinearProgress/>}
                >
                    <Grid container mt={2} mb={2} justifyContent="center" spacing={2}>
                        <>{!loading && resultData.length > 0
                            ? resultData.map((text) => (
                                <Grid
                                    key={`search-result-${text.id}`}
                                    item
                                    xs={12} sm={6} md={4}
                                >
                                    <TextResultCard text={text} onRedirect={handleClose}/>
                                </Grid>
                            ))
                            : <Grid item xs={12} sm={12} md={12} display="flex" justifyContent="center"
                                    alignItems="center">
                                <NoResult/>
                            </Grid>
                        }</>
                        {loading &&
                            <Grid
                                item
                                xs={12} sm={12} md={12}
                                display="flex"
                                justifyContent="center"
                                alignItems="center">
                                <CircularProgress size={80}/>
                            </Grid>}
                    </Grid>
                </InfiniteScroll>
            </DialogContent>
            <DialogActions>
                <Typography ml={3} flexGrow={1}>
                    {t("main.components.search.found", {
                        count: maxResult,
                        s: maxResult > 1 ? "s" : ""
                    })}
                </Typography>
                <Button onClick={handleClose} color="secondary">{t("main.components.button.close")}</Button>
            </DialogActions>
        </Dialog>
    );
}

SearchDialog.defaultProps = {
    open: false,
    onClose: () => {
    },
    value: ""
}